import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import api from 'api';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import type { AppDispatch } from 'redux/store';
import type { BillingRevenueCountryCode, BillingRevenueResponse } from 'types';
import { getErrorMessage } from 'utils/v2/errorHelper';

export function useBillingRevenue(
    providerId: number | null,
    from: string | undefined,
    to: string | undefined,
    countryCodes: BillingRevenueCountryCode[] | undefined
) {
    const dispatch = useDispatch<AppDispatch>();
    const { translate } = useTranslate();
    const [revenue, setRevenue] = useState<BillingRevenueResponse | null>(null);
    const [loading, setLoading] = useState(Boolean(from && to));

    useEffect(() => {
        if (!from || !to) {
            setRevenue(null);
            setLoading(false);
            return;
        }
        setRevenue(null);
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const response = await api.serviceProviderBilling.getRevenue({
                    from,
                    to,
                    ...(providerId == null ? {} : { service_provider_ids: [providerId] }),
                    ...(countryCodes ? { country_codes: countryCodes } : {}),
                });
                if (!cancelled) {
                    setRevenue(response.data);
                }
            } catch (error) {
                if (!cancelled) {
                    dispatch(
                        setAlert({
                            message: getErrorMessage(
                                error,
                                translate('serviceProviderBilling.revenue.loadFailed')
                            ),
                            type: 'error',
                        })
                    );
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        void load();
        return () => {
            cancelled = true;
        };
    }, [countryCodes, dispatch, from, providerId, to, translate]);

    return { revenue, loading };
}
